import { Checkbox, Input, Password, Toast, useToast } from '@resultadosdigitais/tangram-components'
import { ERROR_CODES } from 'constants/errorCodes'
import useRecaptcha from 'hooks/useRecaptcha'
import { useMemo, useReducer, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components/macro'
import useQuery from '../../hooks/useQuery'
import loginService from '../../login-service'
import { isEmptyObject } from '../../object'
import { hasLength, isEmail, isEmpty, validate } from '../../validation'
import Alert from '../Alert'
import Button from '../Button'
import Form from '../Form'
import Link from '../Link'
import Header from './Header'
import { actions, initialState, reducer } from './reducer'

const StyledLink = styled(Link)`
  color: var(--color-primary-70);
  font-size: var(--text-size-md);
  margin-top: var(--size-spacing-0);
`

const StyledCheckbox = styled(Checkbox)`
  margin-top: var(--size-spacing-05);
  margin-bottom: var(--size-spacing-02);
  font-weight: normal;
`

const StyledDiv = styled.div`
  display: flex;
  justify-content: center;
  padding-top: var(--size-spacing-6);
`

function Login({
  showSSOLoginScreen,
  showPasswordRecoverScreen,
  existAccount,
  showMFAScreen,
  showDeviceRegistrationScreen,
  siteKey,
  showMFAChallengeScreen,
  showMFAadoptionScreen,
}) {
  const { t } = useTranslation()
  const query = useQuery()
  const [recaptchaLoadingError, setRecaptchaLoadingError] = useState(false)
  const { recaptchaCompleted, getRecaptchaToken, resetRecaptcha } = useRecaptcha(siteKey, setRecaptchaLoadingError)
  const [remember, setRemember] = useState(false)
  const [isRecaptchaAlertVisible, setRecaptchaAlertVisibility] = useState(false)

  const { addToast } = useToast()

  const [{ formState, error, email, password, validation }, dispatch] = useReducer(reducer, initialState)

  const schema = useMemo(
    () => ({
      email: [
        {
          method: isEmpty,
          validWhen: false,
          message: t('LoginForm.EmptyField'),
        },
        {
          method: isEmail,
          validWhen: true,
          message: t('LoginForm.InvalidEmailField'),
        },
      ],
      password: [
        {
          method: isEmpty,
          validWhen: false,
          message: t('LoginForm.EmptyField'),
        },
        {
          method: value => hasLength(value, { minimum: 1 }),
          validWhen: true,
          message: t('LoginForm.RequiredLength'),
        },
      ],
    }),
    [t],
  )

  const isSubmiting = formState === 'submiting'
  const isError = formState === 'error'
  const isPasswordChanged = useMemo(() => {
    const changedParameter = query.get('password_changed') || ''
    return changedParameter === 'success'
  }, [query])

  const handlePasswordRecoverClick = event => {
    event.preventDefault()
    showPasswordRecoverScreen()
  }

  const handleSSOLoginClick = event => {
    event.preventDefault()
    showSSOLoginScreen()
  }

  const handleEmailChange = event => {
    event.preventDefault()
    if (isSubmiting) return

    const emailLowercased = event.target.value.toLowerCase()
    dispatch({ type: actions.emailChanged, payload: { email: emailLowercased } })

    if (validation?.email) {
      dispatch({ type: actions.validationChanged, payload: { validation: validate({ email: emailLowercased }, schema) } })
    }
  }

  const handlePasswordChange = event => {
    event.preventDefault()

    if (isSubmiting) return

    dispatch({ type: actions.passwordChanged, payload: { password: event.target.value } })

    if (validation?.password) {
      dispatch({ type: actions.validationChanged, payload: { validation: validate({ password: event.target.value }, schema) } })
    }
  }

  const handleFormSubmit = event => {
    event.preventDefault()

    if (isSubmiting) return

    const validation = validate({ email, password }, schema)
    dispatch({ type: actions.validationChanged, payload: { validation } })
    if (isEmptyObject(validation)) submitForm()
  }

  const submitForm = async () => {
    dispatch({ type: actions.loginSubmitted })
    try {
      const redirectTo = query.get('redirect_to') || query.get('return_to') || ''
      const loginChallenge = query.get('login_challenge') || ''
      const zendeskLogin = query.get('zendesk_login') || ''
      const ledgerUuid = query.get('ledger_uuid') || ''

      const payload = {
        email,
        password,
        redirectTo,
        loginChallenge,
        zendeskLogin,
        ledgerUuid,
        remember: remember,
      }

      if (recaptchaLoadingError) {
        payload.recaptchaError = true
      }

      if (recaptchaCompleted) {
        payload.recaptchaToken = getRecaptchaToken()
      }

      const { data } = await loginService.login(payload)

      if (data.show_adoption_screen) {
        showMFAadoptionScreen({ ...data })
        return
      }
      if (data.challenge_mfa_email) {
        showMFAScreen()
        return
      }
      if (data.challenge_device_passcode) {
        showDeviceRegistrationScreen({ ...data, email })
        return
      }
      if (data.challenge_mfa_app) {
        showMFAChallengeScreen({ ...data, email })
        return
      }
      window.location.assign(data.redirectTo)
    } catch (e) {
      console.error('Error on login', e)
      const errorCode = e.response?.data?.error_code
      if (errorCode === ERROR_CODES.RECAPTCHA_REQUIRED) {
        dispatch({ type: actions.recaptchaRequired })
        !recaptchaCompleted && setRecaptchaAlertVisibility(true)
        return
      }
      const isServerError = e.response?.status === 500
      if (isServerError) {
        dispatch({ type: actions.serverError, payload: { error: getErrorTranslationKey(e, t) } })
      } else {
        dispatch({ type: actions.loginErrored, payload: { error: getErrorTranslationKey(e, t) } })
      }

      resetRecaptcha()
    }
  }

  const getQueryString = (queryStrings, queryName) => new URLSearchParams(queryStrings).get(queryName)

  useEffect(() => {
    const queryStringStatus = getQueryString(window.location.search, 'status')
    if (queryStringStatus && queryStringStatus === 'email_pending_verification') {
      addToast({
        title: t('LoginForm.Toast.AlertConfirmMailTitle'),
        message: t('LoginForm.Toast.AlertConfirmMailMessage'),
        kind: Toast.kinds.primary,
      })
    }
    // eslint-disable-next-line
  }, [])

  return (
    <>
      <Form onSubmit={handleFormSubmit} noValidate disabled={isSubmiting} data-testid="login-form">
        <Header existAccount={existAccount} />

        {isError && (
          <>
            <Alert severity="error">{error}</Alert>
          </>
        )}

        <Form.Control error={Boolean(validation?.email)} errorMessage={validation?.email} disabled={isSubmiting}>
          <Form.Label htmlFor="email">{t('LoginForm.Email')}</Form.Label>
          <Input
            type="email"
            data-testid="login-email"
            id="email"
            name="email"
            autoComplete="username"
            value={email}
            onChange={handleEmailChange}
            readOnly={isSubmiting}
          />
          {validation?.email && <Form.Feedback id="field_helper">{validation?.email}</Form.Feedback>}
        </Form.Control>

        <Form.Control error={Boolean(validation?.password)} errorMessage={validation?.password} disabled={isSubmiting}>
          <Form.Label htmlFor="password">{t('LoginForm.Password')}</Form.Label>
          <Password
            id="password"
            name="password"
            data-testid="login-password"
            autoComplete="current-password"
            value={password}
            onChange={handlePasswordChange}
            readOnly={isSubmiting}
          />
          {validation?.password && <Form.Feedback id="field_helper">{validation?.password}</Form.Feedback>}
        </Form.Control>

        <StyledLink href="#" target="_blank" data-testid="login-forgot-password" onClick={handlePasswordRecoverClick}>
          {t('LoginForm.ForgotYourPassword')}
        </StyledLink>

        <div id="recaptcha" />
        <StyledCheckbox
          id="rememberCheckbox"
          name="rememberCheckbox"
          value="checkboxValue"
          label={t('LoginForm.RememberMeCheckbox')}
          onChange={() => setRemember(!remember)}
        />
        {shouldShowRecaptchaErrorMessage(recaptchaCompleted, isRecaptchaAlertVisible) && (
          <Alert severity="error">{t('LoginForm.RecaptchaMessage')}</Alert>
        )}
        {isPasswordChanged && <Alert>{t('LoginForm.PasswordChanged')}</Alert>}
        <Button type="submit" loading={isSubmiting} data-testid="login-submit">
          {t('LoginForm.ButtonNext')}
        </Button>
        <StyledDiv>
          <StyledLink href="#" target="_blank" data-testid="login-sso" onClick={handleSSOLoginClick}>
            {t('LoginForm.SSOLink')}
          </StyledLink>
        </StyledDiv>
      </Form>
    </>
  )
}

function shouldShowRecaptchaErrorMessage(recaptchaCompleted, isRecaptchaAlertVisible) {
  return !recaptchaCompleted && isRecaptchaAlertVisible
}

const getErrorTranslationKey = (error, t) => {
  if (error?.response?.status === 500) return t('LoginForm.WrongServerError')
  if (error?.response?.status === 403) return t('LoginForm.WrongAccessLocation')
  return (
    {
      [ERROR_CODES.DISABLED_USER]: t('LoginForm.WrongDisabledUser'),
      [ERROR_CODES.BLOCKED_USER]: t('LoginForm.WrongBlockedUser'),
    }[error?.response?.data?.error_code] || t('LoginForm.WrongCredentials')
  )
}

export default Login
